import React, { useState } from "react";
import { Button, Upload, message } from "antd";
import { useNavigate } from "react-router-dom";
import { svg } from "../../../Assets/svg";
import { useAppDispatch } from "../../../store";
import { importFramePartsAPI } from "./partRestrctionsApis";
import UploadedSampleFileDisplay from "../../Components/UploadedSampleFileDisplay";
// ✅ 1. Uncomment Imports

const { Dragger } = Upload;

const ImportPartsRestrictions = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  // Instructions based on Figma Screenshot
  const importInstructions = [
    {
      label: "Uprights Height (mm)",
      required: true,
      instruction: "Only Number allowed. (e.g. 1000, 1200, 1800)",
    },
    {
      label: "Horizontal Bracing (Qty)",
      required: true,
      instruction: "Only Number allowed. (e.g. 1, 3, 4, 8)",
    },
    {
      label: "Diagonal Bracing (Qty)",
      required: true,
      instruction: "Only Number allowed. (e.g. 1, 3, 4, 8)",
    },
    {
      label: "Nuts & Bolts (Qty)",
      required: true,
      instruction: "Only Number allowed. (e.g. 1, 3, 4, 8)",
    },
    {
      label: "47mm Spacer (for Footplate)",
      required: true,
      instruction: "Only Number allowed. (e.g. 1, 3, 4, 8)",
    },
    {
      label: "43mm Spacer",
      required: true,
      instruction: "Only Number allowed. (e.g. 1, 3, 4, 8)",
    },
    {
      label: "Frame Depth (mm)",
      required: true,
      instruction: "Only Number allowed. (e.g. 400, 800, 1200)",
    },
    {
      label: "Horizontal Bracing Length (mm)",
      required: true,
      instruction: "Only Number allowed. (e.g. 380, 435, 658)",
    },
    {
      label: "Diagonal Bracing Length (mm)",
      required: true,
      instruction: "Only Number allowed. (e.g. 1000, 1200, 1800)",
    },
    {
      label: "Uprights Width (mm)",
      required: true,
      instruction: "Only Number allowed. (e.g. 82, 100, 120)",
    },
    {
      label: "Thickness (mm)",
      required: true,
      instruction: "Only Number allowed. (e.g. 2, 2.3, 2.5)",
    },
    {
      label: "One Piece Height up to (mm)",
      required: true,
      instruction: "Only Number allowed. (e.g. 3500, 4200, 7500)",
    },
    {
      label: "Finish",
      required: false,
      instruction:
        "Only mentioned 3 value allowed. Can add all 3 together. (e.g. Pre-Galvanised, Powder Coated, Hot Dip Galvanised)",
    },
    {
      label: "Nuts & Bolts Size",
      required: true,
      instruction: "Only mentioned 3 value allowed. (e.g. M10 X 65, M10 X 90)",
    },
  ];

  const handleBack = () => {
    navigate(-1);
  };

  const handleRemoveFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loading) return;
    setFile(null);
  };

  const handleDownloadSample = () => {
    // ✅ 2. Wire up Sample Download
    // dispatch(downloadSampleFramePartsAPI());
    window.open(
      "https://sm-transform-dev.s3.eu-west-1.amazonaws.com/sample-files/Frame%2BParts%2Band%2BAccessories.xlsx",
      "_blank",
    );
  };

  const handleSubmit = () => {
    if (!file) {
      message.error("Please upload a file first.");
      return;
    }

    setLoading(true);
    // ✅ 3. Wire up Import API
    dispatch(
      importFramePartsAPI({
        file: file,
        onSuccess: () => {
          setLoading(false);
          setFile(null);
          // Optional: navigate back to list page after success
          navigate("/parts-restriction");
        },
        onError: () => {
          setLoading(false);
        },
      }),
    );
  };

  const draggerProps = {
    name: "file",
    multiple: false,
    maxCount: 1,
    accept: ".xlsx", // CHANGED: Restricted to .xlsx only
    beforeUpload: (file: File) => {
      // CHANGED: Added strict extension check
      const isXlsx = file.name.toLowerCase().endsWith(".xlsx");
      if (!isXlsx) {
        message.error("You can only upload .xlsx files!");
        return Upload.LIST_IGNORE;
      }
      setFile(file);
      return false;
    },
    onDrop(e: any) {
      console.log("Dropped files", e.dataTransfer.files);
    },
    showUploadList: false,
    style: {
      border: "2px dashed #d9d9d9",
      backgroundColor: "#fafafa",
      padding: "20px 0",
      marginBottom: "30px",
    },
  };

  return (
    <div className="page-content px-40 py-40">
      {/* Header and Action Buttons */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <div
            className="text-primary fs-6 fw-medium d-inline-flex align-items-center backArrowLink mb-0 cursor-pointer"
            onClick={handleBack}
            title="Back"
          >
            <span className="lh-normal">{svg.backArrow}</span>
          </div>
          <h2 className="text-black fs-2 fw-bolder page-title mb-0 ms-2">
            Import Frame Parts, Accessories and Restriction
          </h2>
        </div>
        <div className="d-md-inline-block d-none">
          <button
            onClick={handleBack}
            className="btn btn-outline-secondary btn-lg min-w-126 cancelButton me-lg-4 me-2"
            disabled={loading}
          >
            CANCEL
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-lg min-w-126"
            disabled={loading || !file}
          >
            {/* Show a spinner icon if you have one, or just text */}
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                PROCESSING...
              </>
            ) : (
              "SUBMIT"
            )}
          </button>
        </div>
      </div>

      <div className="card page-card border-0 shadow rounded-4 p-4 p-md-5">
        {/* Conditional File Upload Area */}
        <div className="upload-container">
          {file ? (
            <UploadedSampleFileDisplay
              file={file}
              loading={loading}
              onRemove={handleRemoveFile}
              // Optional: Pass custom style if needed, otherwise it uses default
              // containerStyle={draggerProps.style}
            />
          ) : (
            <Dragger {...draggerProps}>
              <p className="ant-upload-drag-icon text-center">
                {svg?.importIcon}
              </p>
              <p className="ant-upload-text fw-semibold text-center">
                Drag & Drop XLSX File /{" "}
                <span className="text-primary text-decoration-underline">
                  CLICK HERE
                </span>
              </p>
            </Dragger>
          )}
        </div>

        <div className="text-center mt-3 mb-4">
          <Button
            type="link"
            onClick={handleDownloadSample}
            className="text-decoration-underline fw-semibold text-primary fs-6"
          >
            DOWNLOAD SAMPLE XLSX FILE
          </Button>
        </div>

        {/* Mobile Buttons */}
        <div className="d-md-none text-center mb-4">
          <button
            onClick={handleBack}
            className="btn btn-outline-secondary btn-lg min-w-126 cancelButton me-lg-4 me-2"
            disabled={loading}
          >
            CANCEL
          </button>
          <button
            onClick={handleSubmit}
            className="btn btn-primary btn-lg min-w-126"
            disabled={loading || !file}
          >
            {loading ? "IMPORTING..." : "SUBMIT"}
          </button>
        </div>

        {/* Import Instructions */}
        <div className="import-instructions">
          <h3 className="fs-5 fw-bold mb-3">Import XLSX Instruction</h3>

          <ul className="instruction-list ps-3">
            {importInstructions.map((item, index) => (
              <li key={index} className="mb-2 fs-6">
                <span className="fw-bold text-black">
                  {item.label}
                  {item.required ? " (required)" : " (optional)"}
                </span>
                <span className="text-black"> : {item.instruction}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ImportPartsRestrictions;
